/**
 * FriendsMenu — header quick-access Friends panel (Roblox-style).
 * A dropdown off the header Users icon showing a horizontal avatar rail with
 * live green online dots, an "Add Friends" tile, incoming requests with
 * one-tap accept/decline, a mini search, and a "See all" link to /friends.
 * Mirrors the NotificationBell dropdown pattern.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserPlus, Search, Check, X, MessageCircle, ArrowRight, ShieldCheck } from "lucide-react";
import axios from "axios";
import { getStickerEmoji } from "../StickerPicker";
import FriendFace from "../FriendFace";
import PeopleYouMayKnow from "../PeopleYouMayKnow";
import { useFriendConfig } from "../../hooks/useFriendConfig";

const API = process.env.REACT_APP_BACKEND_URL;

const glyph = (c) => getStickerEmoji(c?.sticker) || (c?.display_name || c?.username || "?")[0]?.toUpperCase() || "?";

// One friend in the horizontal rail: avatar circle + green online dot + name.
const RailFriend = ({ f, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1 w-[62px] shrink-0 group"
    data-testid={`rail-friend-${f.id}`}
    title={`${f.display_name || f.username}${f.online ? " · Online" : ""}`}
  >
    <span className="relative inline-flex">
      <FriendFace
        friend={f}
        className="w-12 h-12 rounded-full flex items-center justify-center text-2xl border-2 border-white/15 group-hover:border-violet-400/60 transition-all"
        style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }}
      />
      <span
        className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-slate-900 ${f.online ? "bg-emerald-400" : "bg-slate-500"}`}
        data-testid={`rail-presence-${f.id}`}
      />
    </span>
    <span className="text-white/70 text-[10px] font-bold leading-tight text-center truncate w-full">{f.display_name || f.username}</span>
  </button>
);

export const FriendsMenu = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [me, setMe] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [incomingCount, setIncomingCount] = useState(0);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { config } = useFriendConfig();

  const uid = user && (user.id || user.email);

  // Lightweight count poll for the badge, always running while signed in.
  const fetchCount = useCallback(async () => {
    if (!uid) return;
    try {
      const [c, u] = await Promise.all([
        axios.get(`${API}/api/friends/${uid}/counts`),
        axios.get(`${API}/api/friends/${uid}/unread-count`),
      ]);
      setIncomingCount(c.data?.incoming || 0);
      setUnreadMsgs(u.data?.unread_count || 0);
    } catch { /* silent */ }
  }, [uid]);

  // Full data — only when the panel is open.
  const fetchAll = useCallback(async () => {
    if (!uid) return;
    try {
      const [en, rq] = await Promise.all([
        axios.get(`${API}/api/friends/${uid}/enriched`),
        axios.get(`${API}/api/friends/${uid}/requests-enriched`),
      ]);
      setFriends(en.data.friends || []);
      setMe(en.data.me || null);
      setIncoming(rq.data.incoming || []);
      setIncomingCount((rq.data.incoming || []).length);
    } catch { /* silent */ }
  }, [uid]);

  useEffect(() => { if (uid) fetchCount(); }, [uid, fetchCount]);
  useEffect(() => {
    if (!uid) return;
    const id = setInterval(fetchCount, 15000);
    // Instant: refresh the friends/message badges on any pushed social event.
    const onEvent = () => { fetchCount(); if (open) fetchAll(); };
    window.addEventListener("iw:social-event", onEvent);
    return () => { clearInterval(id); window.removeEventListener("iw:social-event", onEvent); };
  }, [uid, fetchCount, fetchAll, open]);

  // Load full friends + requests when the panel opens, then keep them live
  // with a short poll while it stays open — so accepted friends and new
  // requests appear instantly without needing to reopen the panel.
  useEffect(() => {
    if (!open) return;
    fetchAll();
    const id = setInterval(fetchAll, 10000);
    return () => clearInterval(id);
  }, [open, fetchAll]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced mini-search within the panel.
  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/friends/search`, { params: { user_id: uid, q: term } });
        setResults(r.data.results || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [query, open, uid]);

  const acceptReq = async (fid) => {
    await axios.post(`${API}/api/friends/accept`, { friendship_id: fid });
    fetchAll();
  };
  const declineReq = async (fid) => {
    await axios.post(`${API}/api/friends/decline`, { friendship_id: fid });
    fetchAll();
  };
  const addFriend = async (card) => {
    try {
      await axios.post(`${API}/api/friends/request`, {
        from_user_id: uid,
        from_username: user.username || user.display_name || user.name || user.email,
        to_user_id: card.id,
        to_username: card.username || card.display_name,
      });
      setResults((prev) => prev.map((c) => (c.id === card.id ? { ...c, state: "request_sent" } : c)));
    } catch { /* silent */ }
  };

  const goFriends = (tab) => {
    navigate(tab ? `/friends?tab=${tab}` : "/friends");
    setOpen(false);
  };

  if (!user || user.isDemo) return null;
  if (!config.friends_enabled) return null;

  const onlineCount = friends.filter((f) => f.online).length;
  // Always render the "You" card immediately from the auth user (sticker/initial),
  // then upgrade to the enriched `me` (full avatar/frame) once the fetch resolves.
  const meCard = me || {
    id: uid,
    username: user.username,
    display_name: user.nickname || user.first_name || user.username || "You",
    sticker: user.sticker,
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 rounded-lg text-white/70 hover:text-white transition-colors"
        title="Friends"
        data-testid="header-friends-btn"
      >
        <Users size={18} />
        {(incomingCount + unreadMsgs) > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
            style={{ boxShadow: "0 0 8px rgba(239,68,68,0.5)" }}
            data-testid="header-friends-badge"
          >
            {(incomingCount + unreadMsgs) > 9 ? "9+" : (incomingCount + unreadMsgs)}
          </span>
        )}
      </button>

      {open && (
        <div
          className="
            fixed left-2 right-2 top-[64px] z-50
            sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[360px]
            max-h-[80vh] overflow-y-auto overflow-x-hidden
            rounded-2xl border-2 border-white/15 bg-slate-900 backdrop-blur-md
            shadow-2xl shadow-black/60
          "
          style={{ backgroundColor: "rgba(15, 23, 42, 0.99)" }}
          data-testid="friends-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <div className="flex items-center gap-2">
              <h3 className="text-white text-sm font-fredoka font-bold">Friends</h3>
              <span className="text-white/40 text-xs">({friends.length})</span>
              {onlineCount > 0 && (
                <span className="flex items-center gap-1 text-emerald-400 text-[10px] font-bold" data-testid="friends-online-count">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {onlineCount} online
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => goFriends()} className="text-violet-400/80 hover:text-violet-300 text-[10px] font-bold flex items-center gap-0.5" data-testid="friends-see-all">
                See all <ArrowRight size={11} />
              </button>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="friends-close-btn"
                className="w-6 h-6 rounded-full bg-white/5 text-white/50 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Your own profile — tap to view your avatar & profile */}
          {meCard && (
            <button
              onClick={() => { setOpen(false); navigate("/profile"); }}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-white/8 hover:bg-white/[0.04] transition-colors text-left"
              data-testid="friends-my-profile"
            >
              <span className="relative inline-flex shrink-0">
                <FriendFace
                  friend={meCard}
                  className="w-11 h-11 rounded-full flex items-center justify-center text-xl border-2 border-violet-400/50"
                  style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }}
                  testId="friends-my-face"
                />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{meCard.display_name || meCard.username || "You"} <span className="text-violet-300/80 text-[10px] font-bold">· You</span></p>
                <p className="text-white/40 text-[11px] truncate">{meCard.username ? `@${meCard.username}` : "View my profile"}</p>
              </div>
              <ArrowRight size={14} className="text-white/40 shrink-0" />
            </button>
          )}

          {/* Grown-up shortcut — jump straight to the Parent Corner where a
              parent can see EACH explorer's full friend list + all review
              controls. Hidden for child / solo-explorer accounts. */}
          {user && user.role !== "child_explorer" && (user.account_type || "").toLowerCase() !== "solo_explorer" && (
            <button
              onClick={() => { setOpen(false); navigate("/parent-corner?tab=friends"); }}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-white/8 hover:bg-white/[0.04] transition-colors text-left"
              data-testid="friends-parent-review"
            >
              <span className="w-11 h-11 rounded-full bg-purple-500/20 border-2 border-purple-400/40 flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-purple-300" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">Review your explorers&apos; friends</p>
                <p className="text-white/40 text-[11px] truncate">See each child&apos;s full friend list &amp; safety controls</p>
              </div>
              <ArrowRight size={14} className="text-white/40 shrink-0" />
            </button>
          )}
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search friends by name…"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-white text-xs focus:outline-none focus:border-violet-500/50"
                data-testid="friends-menu-search"
                autoCapitalize="off" autoCorrect="off" spellCheck="false"
              />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            </div>
          </div>

          {/* Search results (only when typing) */}
          {query.trim().length >= 2 && (
            <div className="px-4 pb-2" data-testid="friends-menu-results">
              {searching ? (
                <p className="text-white/40 text-[11px] py-2">Searching…</p>
              ) : results.length === 0 ? (
                <p className="text-white/40 text-[11px] py-2">No one found.</p>
              ) : (
                <div className="space-y-1.5">
                  {results.slice(0, 5).map((c) => (
                    <div key={c.id} className="flex items-center gap-2.5 py-1" data-testid={`menu-result-${c.id}`}>
                      <FriendFace friend={c} className="w-8 h-8 rounded-full flex items-center justify-center text-base border border-white/15 shrink-0"
                        style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-bold truncate">{c.display_name || c.username}</p>
                        {c.username && <p className="text-white/40 text-[10px] truncate">@{c.username}</p>}
                      </div>
                      {c.state === "friends" ? (
                        <span className="text-violet-300 text-[10px] font-bold px-2">Friends</span>
                      ) : c.state === "request_sent" ? (
                        <span className="text-white/40 text-[10px] font-bold px-2" data-testid={`menu-sent-${c.id}`}>Requested</span>
                      ) : c.state === "respond" ? (
                        <button onClick={() => goFriends("requests")} className="text-amber-300 text-[10px] font-bold px-2 py-1 rounded-lg bg-amber-500/15 hover:bg-amber-500/25" data-testid={`menu-respond-${c.id}`}>Respond</button>
                      ) : (
                        <button onClick={() => addFriend(c)} className="text-emerald-300 text-[10px] font-bold px-2 py-1 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 flex items-center gap-1" data-testid={`menu-add-${c.id}`}>
                          <UserPlus size={11} /> Add
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Incoming requests */}
          {incoming.length > 0 && query.trim().length < 2 && (
            <div className="px-4 pb-2">
              <p className="text-amber-300/80 text-[10px] font-bold uppercase tracking-wider mb-1.5">Requests ({incoming.length})</p>
              <div className="space-y-1.5">
                {incoming.slice(0, 4).map((r) => (
                  <div key={r.friendship_id} className="flex items-center gap-2.5" data-testid={`menu-request-${r.friendship_id}`}>
                    <FriendFace friend={r} className="w-8 h-8 rounded-full flex items-center justify-center text-base border border-amber-400/30 shrink-0"
                      style={{ background: "linear-gradient(135deg, #f59e0b, #8B5CF6)" }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-bold truncate">{r.display_name || r.username}</p>
                      <p className="text-white/40 text-[10px]">Wants to be friends</p>
                    </div>
                    <button onClick={() => acceptReq(r.friendship_id)} className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid={`menu-accept-${r.friendship_id}`} title="Accept"><Check size={14} /></button>
                    <button onClick={() => declineReq(r.friendship_id)} className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid={`menu-decline-${r.friendship_id}`} title="Decline"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Horizontal friends rail (Roblox-style) */}
          {query.trim().length < 2 && (
            <div className="px-4 pt-1 pb-4 border-t border-white/8">
              <div className="flex items-stretch gap-3 overflow-x-auto pt-3 pb-1" style={{ scrollbarWidth: "thin" }} data-testid="friends-rail">
                {/* Add Friends leading tile */}
                <button onClick={() => goFriends("add")} className="flex flex-col items-center gap-1 w-[62px] shrink-0 group" data-testid="rail-add-friends">
                  <span className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-dashed border-white/25 text-white/50 group-hover:border-emerald-400/60 group-hover:text-emerald-400 transition-all">
                    <UserPlus size={20} />
                  </span>
                  <span className="text-white/60 text-[10px] font-bold leading-tight text-center">Add</span>
                </button>

                {friends.length === 0 ? (
                  <div className="flex-1 flex items-center">
                    <p className="text-white/40 text-[11px]">No friends yet — tap Add to find some!</p>
                  </div>
                ) : (
                  friends.map((f) => (
                    <RailFriend key={f.id} f={f} onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("iw:open-friend-profile", { detail: { targetId: f.id } })); }} />
                  ))
                )}
              </div>

              {friends.length > 0 && (
                <button onClick={() => goFriends()} className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-xs font-bold transition-all" data-testid="friends-open-chat">
                  <MessageCircle size={14} /> Open Friends & Chat
                </button>
              )}

              <div className="mt-3">
                <PeopleYouMayKnow uid={uid} fromUsername={user.username || user.display_name || user.name || user.email} variant="rail" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FriendsMenu;
